import { ATLAN } from '@elementary/common-ui/constants/queryKeys';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as atlanApi from 'shared/services/atlanApi';
import { AtlanIntegrationForm, AtlanIntegrationResponse } from 'shared/types';

type Props = {
  envId?: string;
};

const useAtlanIntegration = ({ envId }: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const queryKey = [ATLAN, envId];

  const atlanQuery = useQuery(
    queryKey,
    () => atlanApi.fetchAtlanIntegration(envId!),
    {
      enabled: !!envId,
    },
  );

  const testMutation = useMutation<void, MutationError, AtlanIntegrationForm>(
    (data) => atlanApi.testAtlanIntegration(envId!, data),
  );

  const saveMutation = useMutation<
    AtlanIntegrationResponse,
    MutationError,
    AtlanIntegrationForm
  >(
    (data) =>
      atlanQuery.data?.connected
        ? atlanApi.updateAtlanIntegration(envId!, data)
        : atlanApi.createAtlanIntegration(envId!, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        openSnackbar({
          message: atlanQuery.data?.connected
            ? 'Atlan connection updated'
            : 'Atlan connected successfully',
          variant: 'success',
        });
      },
    },
  );

  const deleteMutation = useMutation<void, MutationError>(
    () => atlanApi.deleteAtlanIntegration(envId!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        openSnackbar({
          message: 'Atlan connection removed',
          variant: 'success',
        });
      },
    },
  );

  return {
    atlanQuery,
    testMutation,
    saveMutation,
    deleteMutation,
  };
};

export default useAtlanIntegration;
